import { useState } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/AuthContext';

const INDUSTRIES = [
    'E-commerce',
    'Agency/Developer',
    'Local Business',
    'Enterprise',
    'Legal Professional',
    'Other',
];

const LEGAL_INDUSTRIES = ['Legal Professional'];

export default function OnboardingPage() {
    const { user, setProfile, refreshClaims } = useAuth();
    const [companyName, setCompanyName] = useState('');
    const [industry, setIndustry] = useState('');
    const [lawyerVerified, setLawyerVerified] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const isLegalIndustry = LEGAL_INDUSTRIES.includes(industry);
    const canSubmit = industry && (!isLegalIndustry || lawyerVerified);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;

        setLoading(true);
        setError('');

        try {
            const profileData = {
                email: user.email,
                company: companyName, // Storing Company Name
                industry,
                lawyerVerified: isLegalIndustry ? lawyerVerified : false,
                onboardingComplete: true,
                createdAt: serverTimestamp(),
            };

            await setDoc(
                doc(db, 'users', user.uid),
                profileData,
                { merge: true }
            );

            // Update local state to trigger re-render / route change
            setProfile(profileData);

            // Wait briefly for the Cloud Function to set custom claims,
            // then refresh the token so the claims are available locally
            setTimeout(async () => {
                await refreshClaims();
            }, 2000);
        } catch (err) {
            console.error('Onboarding error:', err);
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--surface)] p-4 relative overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--accent-glow)_0%,transparent_70%)] opacity-20"></div>
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[var(--accent)] to-transparent opacity-30"></div>

            <div className="w-full max-w-lg relative z-10">
                <div className="text-center mb-12">
                    <h1 className="text-3xl font-black text-[var(--text)] uppercase tracking-[0.4em] ">Welcome to Accessibility Scout</h1>
                    <p className="text-[0.625rem] text-[var(--accent)] font-black uppercase tracking-[0.3em] mt-3 opacity-60">
                        Please complete your profile to continue
                    </p>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="bg-[var(--surface-elevated)] border-2 border-[var(--border)] p-10 space-y-8 rounded-none  shadow-[0.25em_0.25em_0em_var(--border)] relative"
                >
                    <div className="absolute top-0 right-10 w-px h-10 bg-gradient-to-b from-[var(--accent)] to-transparent opacity-40"></div>

                    {error && (
                        <div className="bg-[var(--error)]/10 border-2 border-[var(--error)] p-4 rounded-none text-[var(--error)] text-[0.625rem] font-black uppercase tracking-widest text-center animate-shake">
                            Error: {error}
                        </div>
                    )}

                    {/* Company Name */}
                    <div className="space-y-4">
                        <label className="text-[0.625rem] font-black text-[var(--text-muted)] uppercase tracking-[0.2em] flex items-center gap-3" htmlFor="companyName">
                            <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-none shadow-[0.25em_0.25em_0em_var(--border)]"></span>
                            Company Name
                        </label>
                        <input
                            id="companyName"
                            type="text"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            required
                            placeholder="YOUR COMPANY (O.G. AGENCY INC.)"
                            className="w-full px-6 py-4 bg-[var(--surface-elevated)] border-2 border-[var(--border)] text-[var(--text)] font-black text-[0.625rem] tracking-widest uppercase rounded-none focus:outline-none focus:border-[var(--accent)] focus:shadow-[0.25em_0.25em_0em_var(--border)] transition-all"
                        />
                    </div>

                    {/* Industry Selector */}
                    <div className="space-y-4">
                        <label className="text-[0.625rem] font-black text-[var(--text-muted)] uppercase tracking-[0.2em] flex items-center gap-3" htmlFor="industry">
                            <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-none shadow-[0.25em_0.25em_0em_var(--border)]"></span>
                            Select Your Industry
                        </label>
                        <div className="relative group">
                            <select
                                id="industry"
                                value={industry}
                                onChange={(e) => {
                                    setIndustry(e.target.value);
                                    setLawyerVerified(false);
                                }}
                                required
                                className="w-full px-6 py-4 bg-[var(--surface-elevated)] border-2 border-[var(--border)] text-[var(--text)] font-mono text-sm rounded-none appearance-none cursor-pointer focus:outline-none focus:border-[var(--accent)] focus:shadow-[0.25em_0.25em_0em_var(--border)] transition-all group-hover:border-[var(--accent)]/40"
                            >
                                <option value="" className="bg-[var(--surface)] text-[var(--text-muted)]">
                                    [ Choose Your Industry ]
                                </option>
                                {INDUSTRIES.map((ind) => (
                                    <option key={ind} value={ind} className="bg-[var(--surface)] text-[var(--text)]">
                                        {ind.toUpperCase()}
                                    </option>
                                ))}
                            </select>
                            <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-[var(--accent)] opacity-50">
                                ▼
                            </div>
                        </div>
                    </div>

                    {/* Legal Gate */}
                    {isLegalIndustry && (
                        <div className="bg-[var(--surface-elevated)] border-2 border-[var(--accent)] p-6 space-y-6 animate-in slide-in-from-bottom-5 rounded-none relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-1 h-full bg-[var(--accent)] shadow-[0.25em_0.25em_0em_var(--border)]"></div>
                            <div className="flex gap-4">
                                <span className="text-2xl filter grayscale opacity-70">⚖️</span>
                                <div>
                                    <h3 className="text-[var(--accent)] text-lg font-black font-black text-xs uppercase tracking-[0.2em] ">
                                        Verification Required
                                    </h3>
                                    <p className="text-[0.625rem] text-[var(--text-muted)] font-black uppercase tracking-widest mt-2 leading-relaxed opacity-80">
                                        Access restricted. Please confirm your account type and use case below:
                                    </p>
                                </div>
                            </div>

                            <label className="flex items-start gap-4 cursor-pointer group p-4 bg-[var(--surface-elevated)] border border-white/5 rounded-none hover:bg-[var(--surface-elevated)] transition-all" htmlFor="lawyer-verify">
                                <div className="mt-1 relative h-5 w-5 shrink-0 border-2 border-[var(--accent)] rounded flex items-center justify-center group-hover:border-[var(--accent)] transition-all">
                                    <input
                                        id="lawyer-verify"
                                        type="checkbox"
                                        checked={lawyerVerified}
                                        onChange={(e) => setLawyerVerified(e.target.checked)}
                                        className="h-3 w-3 appearance-none checked:bg-[var(--accent)] transition-all cursor-pointer"
                                    />
                                </div>
                                <span className="text-[0.625rem] text-[var(--text-muted)] font-black uppercase tracking-[0.15em] leading-relaxed group-hover:text-[var(--text)] transition-colors">
                                    I am actively supporting a client who is being sued for compliance, and I am using this tool solely to validate their digital assets.
                                </span>
                            </label>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={!canSubmit || loading}
                        className="w-full bg-[var(--accent)] text-black py-5 mt-6 text-[0.75rem] font-black uppercase tracking-[0.4em] rounded-none hover:shadow-[0.25em_0.25em_0em_var(--border)] transition-all disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed cursor-pointer transform active:scale-95"
                    >
                        {loading ? 'Setting up your account...' : 'Go to Dashboard'}
                    </button>
                </form>
            </div>
        </div>
    );
}
